/**
 * sensorLabels.js — Libellés capteur unifiés pour toute l'application.
 * Source unique : modifier ici se répercute partout (liste, comparaison, PDF,
 * administration, Mode Pro Vétérinaire).
 *
 * La marque se déduit de la session : `importFormat` désigne un capteur tiers
 * (« Capteur externe », sans nom de marque, D199), sinon `sensorType` distingue
 * EkynAir Live d'EkynAir Breathe. Le mode (licol / statique / déplacement) reste
 * porté par `sensorType`. « EkynAir Breathe » / « EkynAir Live » = invariants
 * (marque) ; « Capteur externe » et les modes passent par i18n. Mode entre
 * parenthèses après la marque (« Capteur externe (Statique) ») : ni tiret
 * cadratin, ni point médian, déjà séparateur des cartes et de la légende de
 * comparaison (D217). Type absent : « Non renseigné ».
 */

// Formats d'import issus d'un capteur tiers (backend/constants/importFormats.js).
export const EXTERNAL_IMPORT_FORMATS = Object.freeze(['atmotube_pro2']);

const BREATHE_MODES = Object.freeze(['halter', 'static', 'moving']);

// La page du lien de séance reçoit le booléen dérivé `externalSensor` (D247),
// jamais importFormat.
export function isExternalSensor(session) {
  if (typeof session?.externalSensor === 'boolean') return session.externalSensor;
  return EXTERNAL_IMPORT_FORMATS.includes(session?.importFormat);
}

/** Marque seule : « Capteur externe », « EkynAir Live » ou « EkynAir Breathe ». */
export function sensorBrand(session, t) {
  if (isExternalSensor(session)) return t('sessions.sensor.external');
  if (session?.sensorType === 'ekynairlive') return 'EkynAir Live';
  return 'EkynAir Breathe';
}

/**
 * sensorLabel(session, t) — libellé court « Marque (Mode) » (liste, filtre,
 * carte, comparaison). `t` = fonction react-i18next de l'appelant.
 */
export function sensorLabel(session, t) {
  const type = session?.sensorType;
  if (BREATHE_MODES.includes(type)) return `${sensorBrand(session, t)} (${t(`sessions.sensor.${type}`)})`;
  if (type === 'pmscan' || type === 'ekynairlive') return sensorBrand(session, t);
  return type || t('common.notProvided');
}

/**
 * sensorLabelLong(session, t) — libellé descriptif « Marque (Mode détaillé) »
 * d'une session (PDF propriétaire).
 */
export function sensorLabelLong(session, t) {
  const type = session?.sensorType;
  if (BREATHE_MODES.includes(type)) return `${sensorBrand(session, t)} (${sensorModeLabelLong(type, t)})`;
  if (type === 'ekynairlive') return sensorModeLabelLong(type, t);
  if (type === 'pmscan') return sensorBrand(session, t);
  return type || t('common.notProvided');
}

// Le nom BLE commence par un préfixe constructeur qui ne doit jamais
// apparaître : seuls ses derniers caractères identifient le capteur.
const BLE_ID_VISIBLE_CHARS = 6;
const UNKNOWN_BLE_ID = 'unknown';

/**
 * sensorIdentifier(session, t) — ligne « identifiant capteur » du PDF, lue sur
 * la SESSION uniquement (jamais Horse.equipmentId : le capteur du cheval peut
 * ne pas être celui qui a produit les mesures).
 *   - EkynAir Live : « IMEI » + session.sensorImei ;
 *   - EkynAir Breathe : « Identifiant capteur » + 6 derniers caractères de
 *     l'identifiant BLE (mobileMetadata.sensorId, sessions smartphone) ;
 *   - Capteur externe : aucune ligne.
 * Identifiant absent → null (ligne omise).
 * @returns {{ label: string, value: string } | null}
 */
export function sensorIdentifier(session, t) {
  if (!session || isExternalSensor(session)) return null;
  if (session.sensorType === 'ekynairlive') {
    return session.sensorImei ? { label: 'IMEI', value: session.sensorImei } : null;
  }
  const bleId = session.mobileMetadata?.sensorId?.trim();
  if (!bleId || bleId === UNKNOWN_BLE_ID) return null;
  return { label: t('pdf.info.sensorId'), value: bleId.slice(-BLE_ID_VISIBLE_CHARS) };
}

/**
 * sensorModeLabelLong(type, t) — description du MODE seul, pour les radios de
 * choix capteur (SessionEditFields, CsvUploadPage) : on choisit un mode, la
 * marque n'y figure pas. Traduit via sessions.sensor.long.*.
 */
export function sensorModeLabelLong(type, t) {
  switch (type) {
    case 'halter': return t('sessions.sensor.long.halter');
    case 'static': return t('sessions.sensor.long.static');
    case 'moving': return t('sessions.sensor.long.moving');
    case 'ekynairlive': return t('sessions.sensor.long.ekynairlive');
    case 'pmscan': return 'EkynAir Breathe';
    default: return type || '—';
  }
}
